import React, { useRef, useState, useContext } from 'react'
import { ModalContext, ModalInjectedProps } from 'containers/ModalManager'

// Components
import Modal from 'components/Modal'
import { Button } from '@sketch/components'

// Local Components
import UserChip from '../../components/UserChip'
import SignInWithPasswordForm from './SignInWithPasswordForm'

import useUserProfile from 'modules/user/useUserProfile'
import MfaVerificationCodeModal from 'modules/user/modals/MfaAuthenticationModal'

// Styles
import { UserChipContainer, Label } from './SignInWithPasswordModal.styles'

// Types
export interface OnlySignInWithPasswordModalProps {
  workspaceName: string
  name: string
  email: string
  avatarSrc?: string
  onCompleted?: () => void
}

type SignInWithPasswordModalProps = OnlySignInWithPasswordModalProps &
  ModalInjectedProps

const SignInWithPasswordModal: React.FC<SignInWithPasswordModalProps> = ({
  workspaceName,
  name,
  email,
  avatarSrc,
  onCompleted,
  hideModal,
}) => {
  const { showModal } = useContext(ModalContext)

  const { data } = useUserProfile()
  const isMfaEnabled = data?.me.mfaState === 'ENABLED'

  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)

  return (
    <Modal
      title={`Sign in to "${workspaceName}" Workspace`}
      onCancel={hideModal}
    >
      <Modal.Body>
        <UserChipContainer>
          <Label>Sketch Account</Label>
          <UserChip name={name} email={email} avatarSrc={avatarSrc} />
        </UserChipContainer>
        <SignInWithPasswordForm
          submitButtonRef={submitButtonRef}
          email={email}
          onSubmit={() => setIsLoading(true)}
          onCompleted={data => {
            hideModal()

            if (isMfaEnabled && data.signIn.__typename === 'MFACredentials') {
              showModal(MfaVerificationCodeModal, {
                mfaToken: data.signIn.mfaToken,
                onSuccess: onCompleted,
              })
            } else {
              onCompleted?.()
            }
          }}
          onError={() => setIsLoading(false)}
          onDisableSubmit={disabled => setDisableSubmit(disabled)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={isLoading}
          onClick={() => hideModal()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isLoading || disableSubmit}
          loading={isLoading}
          onClick={() => submitButtonRef.current?.click()}
        >
          Sign In
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SignInWithPasswordModal
